import React from "react";

// -----------------------------------------------------------------------
// MapThumbnail — procedural SVG thumbnails for Wardogs map variants.
// No external images: every variant has a distinct palette + icon motif.
// Falls back to a generic sector-grid thumbnail for unknown names.
// -----------------------------------------------------------------------

const PALETTES = {
  Bakurani: {
    c1: "#2d4a1e", c2: "#5c7a2f", c3: "#8aad44", accent: "#e2f0a1",
    // Alpine village — mountain triangle + pine
    icon: (
      <g>
        <polygon points="20,70 48,30 76,70" fill="rgba(255,255,255,0.14)" />
        <polygon points="38,70 60,42 82,70" fill="rgba(255,255,255,0.22)" />
        <circle cx="98" cy="40" r="7" fill="rgba(255,230,120,0.7)" />
      </g>
    ),
  },
  // Ozeti — Western European wooded river basin: town, football stadium,
  // farmland, church on a hill (internal game-file name: "Madrid").
  Ozeti: {
    c1: "#5a1818", c2: "#9c3b1a", c3: "#d87a3a", accent: "#ffe6b0",
    icon: (
      <g>
        <rect x="24" y="42" width="12" height="28" fill="rgba(0,0,0,0.35)" />
        <rect x="40" y="30" width="12" height="40" fill="rgba(0,0,0,0.45)" />
        <rect x="56" y="38" width="12" height="32" fill="rgba(0,0,0,0.35)" />
        <rect x="72" y="24" width="12" height="46" fill="rgba(0,0,0,0.5)" />
        <rect x="88" y="34" width="12" height="36" fill="rgba(0,0,0,0.4)" />
      </g>
    ),
  },
  // Zestafona — North American river-bend map with settlement clusters,
  // brick warehouses and farmland (internal game-file name: "Detroit").
  Zestafona: {
    c1: "#3a2c17", c2: "#6b4f26", c3: "#b48b43", accent: "#f6dca2",
    icon: (
      <g>
        <rect x="26" y="40" width="8" height="30" fill="rgba(255,255,255,0.3)" />
        <rect x="40" y="34" width="10" height="36" fill="rgba(255,255,255,0.35)" />
        <circle cx="78" cy="42" r="14" stroke="rgba(255,255,255,0.5)" strokeWidth="3" fill="none" />
        <circle cx="78" cy="42" r="5" fill="rgba(255,255,255,0.5)" />
        <rect x="94" y="38" width="8" height="32" fill="rgba(255,255,255,0.25)" />
      </g>
    ),
  },
  // Legacy aliases — kept so previously-cached servers still render a tile.
  Madrid: {
    c1: "#5a1818", c2: "#9c3b1a", c3: "#d87a3a", accent: "#ffe6b0",
    icon: (
      <g>
        <rect x="24" y="42" width="12" height="28" fill="rgba(0,0,0,0.35)" />
        <rect x="72" y="24" width="12" height="46" fill="rgba(0,0,0,0.5)" />
      </g>
    ),
  },
  Detroit: {
    c1: "#3a2c17", c2: "#6b4f26", c3: "#b48b43", accent: "#f6dca2",
    icon: (
      <g>
        <circle cx="78" cy="42" r="14" stroke="rgba(255,255,255,0.5)" strokeWidth="3" fill="none" />
      </g>
    ),
  },
};

const FALLBACK = {
  c1: "#1a1f1c", c2: "#2a3830", c3: "#4a5c52", accent: "#a0bd42",
  icon: (
    <g>
      <rect x="30" y="25" width="60" height="50" fill="rgba(255,255,255,0.08)" />
      <line x1="30" y1="40" x2="90" y2="40" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <line x1="30" y1="55" x2="90" y2="55" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <line x1="50" y1="25" x2="50" y2="75" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
      <line x1="70" y1="25" x2="70" y2="75" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
    </g>
  ),
};

export const MapThumbnail = ({ variant, base, width = 120, height = 68, label = true, testId }) => {
  const key = (variant || "").trim();
  const palette = PALETTES[key] || FALLBACK;
  const vb = `0 0 128 72`;

  // Real screenshots for the three playable Wardogs maps. Keys match the
  // translated public names from the backend (see _WARDOGS_MAP_ALIAS).
  const PHOTO = {
    Bakurani: "/maps/bakurani.webp",
    Ozeti: "/maps/ozeti.webp",
    Zestafona: "/maps/zestafona.webp",
  };
  const photoSrc = PHOTO[key];
  const clipId = `mt-clip-${key || "x"}`;

  return (
    <svg
      viewBox={vb}
      width={width}
      height={height}
      preserveAspectRatio="xMidYMid slice"
      style={{ display: "block", border: `1px solid ${palette.accent}44`, borderRadius: 2 }}
      aria-label={`${variant || "Unknown"} map thumbnail`}
      data-testid={testId}
    >
      <defs>
        <linearGradient id={`mg-${key}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={palette.c1} />
          <stop offset="60%" stopColor={palette.c2} />
          <stop offset="100%" stopColor={palette.c3} />
        </linearGradient>
        <pattern id={`mg-${key}-grid`} width="12" height="12" patternUnits="userSpaceOnUse">
          <path d="M0 0 L0 12 L12 12" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
        </pattern>
        <clipPath id={clipId}>
          <rect x="0" y="0" width="128" height="72" />
        </clipPath>
      </defs>

      {photoSrc ? (
        <>
          <image
            href={photoSrc}
            xlinkHref={photoSrc}
            x="0" y="0" width="128" height="72"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${clipId})`}
          />
          {/* subtle grid overlay to keep HUD feel */}
          <rect x="0" y="0" width="128" height="72" fill={`url(#mg-${key}-grid)`} opacity="0.5" />
        </>
      ) : (
        <>
          <rect x="0" y="0" width="128" height="72" fill={`url(#mg-${key}-bg)`} />
          <rect x="0" y="0" width="128" height="72" fill={`url(#mg-${key}-grid)`} />
          {palette.icon}
        </>
      )}

      {/* corner brackets */}
      <path d="M3 3 L9 3 M3 3 L3 9" stroke={palette.accent} strokeWidth="1.5" fill="none" opacity="0.9" />
      <path d="M125 69 L119 69 M125 69 L125 63" stroke={palette.accent} strokeWidth="1.5" fill="none" opacity="0.9" />

      {label && (
        <>
          {!photoSrc && <rect x="0" y="56" width="128" height="16" fill="rgba(0,0,0,0.55)" />}
          {photoSrc && <rect x="0" y="56" width="128" height="16" fill="rgba(0,0,0,0.7)" />}
          <text
            x="6" y="68"
            fontFamily="Share Tech Mono, monospace"
            fontSize="10"
            letterSpacing="1.5"
            fill={palette.accent}
          >
            {(variant || "UNKNOWN").toUpperCase()}
          </text>
          {base && (
            <text
              x="122" y="68"
              textAnchor="end"
              fontFamily="Share Tech Mono, monospace"
              fontSize="8"
              fill="rgba(255,255,255,0.75)"
            >
              {base.toUpperCase()}
            </text>
          )}
        </>
      )}
    </svg>
  );
};

export default MapThumbnail;
